import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../widgets/button/button';
import './organization-detail.css';
import { Rating } from '../../widgets/rating/rating';
import { ReactComponent as ArrowRight } from '../../../assets/arrow-right.svg';
import { Loading } from '../../widgets/loading/loading';
import { convertToTitleCase, formatURL } from '../../../utils/text';
import { currencyFormatter, ratioFormatter } from '../../../utils/numbers';
import { useNavigation } from '../../../contexts/navigation-context';
import '@arcgis/map-components/dist/components/arcgis-map';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import Graphic from '@arcgis/core/Graphic.js';
import Map from '@arcgis/core/Map.js';
import saltExchangeLogo from '../../../assets/loading.png';
import * as cn from 'classnames';

export const OrganizationDetailPage = () => {
  const [organizationData, setOrganizationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentPage } = useNavigation();
  const mapViewRef = useRef(null);

  const configureMap = () => {
    if (!!mapViewRef.current?.map) {
      mapViewRef.current.center = [organizationData.LNG, organizationData.LAT];
      const point = {
        //Create a point
        type: 'point',
        longitude: organizationData.LNG,
        latitude: organizationData.LAT,
      };
      const simpleMarkerSymbol = {
        type: 'simple-marker',
        color: [226, 119, 40], // Orange
        outline: {
          color: [255, 255, 255], // White
          width: 1,
        },
      };

      const map = mapViewRef.current.map;

      // console.log('map = %o, mapView is = %o', map, mapViewRef.current);
      map.layers.forEach((l) => {
        // console.log('layer l.id = %o, l.title = %o, l = %o', l.id, l.title, l);
        if (l.id === '195cb47d185-layer-2') {
          // All CSEC Orgs layer
          l.visible = false;
        }
      });

      const pointGraphic = new Graphic({
        geometry: point,
        symbol: simpleMarkerSymbol,
      });
      let layer = new GraphicsLayer({
        graphics: [pointGraphic],
      });
      map.layers.push(layer);
    }
  };

  const mapCB = useCallback(
    (mapView) => {
      if (mapView === null) {
        // DOM node referenced by ref has been unmounted
      } else {
        mapViewRef.current = mapView;
        mapView.addEventListener('arcgisViewReadyChange', () => {
          if (organizationData) {
            configureMap();
          }
        });
      }
    },
    [organizationData],
  );

  useEffect(() => {
    if (organizationData) {
      if (mapViewRef.current) {
        configureMap();
      }
    }
  }, [organizationData]);

  const loadOrganizations = async () => {
    setIsLoading(true);
    if (currentPage?.params?.organizationId) {
      const result = await fetch(process.env.REACT_APP_API_URL + '/organizations/' + currentPage.params.organizationId);
      const orgData = await result.json();
      setOrganizationData(orgData);
      console.log('organizationData = %o', orgData);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadOrganizations();
  }, [currentPage]);

  return (
    <div className="organization-detail-page">
      {isLoading && (
        <div>
          <Loading />
        </div>
      )}
      {!isLoading && !organizationData && <div className="organization-empty-message">No matching organization found</div>}
      {!isLoading && organizationData && (
        <div className="od-content-container">
          <div className="od-top-container">
            <div className="od-details-container">
              <div className="page-subtitle">Organization</div>
              <div className="od-org-name text-xlarge text-white">{convertToTitleCase(organizationData.NAME)}</div>
              <div class="column-container">
                <div class="column">
                  <div className="text-gray text-small">Charity Navigator Rating</div>
                  <div className="od-rating-container text-gray">
                    <Rating rating={organizationData.CHARITY_NAVIGATOR_RATING}></Rating>
                  </div>
                </div>
                <div class="column">
                  <div className="text-gray text-small">Pre-screened</div>
                  <div className="od-rating-container text-gray">
                    <img className={cn('is-pre-screened-icon', !organizationData.IS_SCREENED && 'not-screened')} src={saltExchangeLogo} alt="Pre-screened" />
                  </div>
                </div>
              </div>

              <div className="text-gray">{organizationData.DESCRIPTION}</div>
              <div className="od-details-buttons-container">
                {formatURL(organizationData.WEBSITE_URL) && (
                  <Button onClick={() => window.open(formatURL(organizationData.WEBSITE_URL), '_blank')}>Visit website</Button>
                )}
                {formatURL(organizationData.DONATION_URL) && (
                  <Button onClick={() => window.open(formatURL(organizationData.DONATION_URL), '_blank')} icon={<ArrowRight></ArrowRight>} color="highlight">
                    Donate
                  </Button>
                )}
              </div>
            </div>
            <div className="od-map-container">
              {!(organizationData?.LAT && organizationData?.LNG) && (
                <div class="location-not-available-message text-light text-small text-gray">
                  Location information is not available
                  <br /> for this organization
                </div>
              )}
              {organizationData?.LAT && organizationData?.LNG && <arcgis-map ref={mapCB} item-id="1fccf76daeb74de586a4c799ba1ff42a"></arcgis-map>}
            </div>
          </div>
          <div className="od-financial-data-container">
            <div className="text-large text-white">Financial Data</div>
          </div>

          <div className="od-financial-data-items-container">
            {(!financials || financials.length === 0) && <div className="text-normal text-gray-2 text-regular">No financial data available</div>}
            {financials.length > 0 &&
              financials?.map((fd) => (
                <div className="od-financial-data-item" key={fd.id}>
                  <div>
                    <div className="od-fd-title text-gray">{fd.title}</div>
                    {organizationData.financials
                      .sort((f1, f2) => (f1.TAX_YEAR > f2.TAX_YEAR ? -1 : 1))
                      .map((f) => (
                        <div className="od-financial-value-item text-small">
                          <div className="od-year text-gray">{f.TAX_YEAR}</div>
                          <div className="od-value text-white">
                            {fd.isCurrency ? currencyFormatter.format(f[fd.field]) : ratioFormatter.format(f[fd.field])}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

const financials = [
  { id: 1, title: 'Revenue', field: 'REVENUE', isCurrency: true },
  { id: 2, title: 'Expenses', field: 'EXPENSES', isCurrency: true },
  { id: 3, title: 'Assets', field: 'ASSETS', isCurrency: true },
  { id: 4, title: 'Program Revenue', field: 'PROGRAM_REVENUE', isCurrency: true },
  { id: 5, title: 'Contributions', field: 'CONTRIBUTIONS', isCurrency: true },
  { id: 6, title: 'Revenue Less Expenses', field: 'REVENUE_LESS_EXPENSES', isCurrency: true },
  { id: 7, title: 'Expense Ratio', field: 'EXPENSE_RATIO', isCurrency: false },
  { id: 8, title: 'Working Capital Ratio', field: 'WORKING_CAPITAL_RATIO', isCurrency: false },
  { id: 9, title: 'Months of Burnable Assets', field: 'MONTHS_OF_BURNABLE_ASSETS', isCurrency: false },
  { id: 10, title: 'Officer Comp to Assets', field: 'OFFICER_COMP_TO_ASSETS', isCurrency: false },
];
